import type { WriteRequest } from "@aws-sdk/client-dynamodb";
import { AwsBatchWriteQueue } from "@models/storage/AwsBatchWriteQueue";
import { loadCloudConfig, type CloudConfig } from "@models/storage/cloudConfig";
import type { ITelemetryService, TelemetryEnvelope } from "./ITelemetryService";
import { TELEMETRY_EVENT_DENYLIST } from "./TelemetryEventDenylist";

const LIFECYCLE_EVENT_TYPES = new Set([
  "launch_application",
  "quit_application",
  "open_project",
  "close_project",
]);

/** AWS DynamoDB telemetry writer matching Unity's `AwsTelemetryModel`. */
export class AwsTelemetryModel implements ITelemetryService {
  private readonly m_queue: AwsBatchWriteQueue;

  constructor(config: CloudConfig = loadCloudConfig()) {
    this.m_queue = new AwsBatchWriteQueue(config.telemetryTableName, config);
  }

  capture(envelope: TelemetryEnvelope): void {
    if (
      envelope.source === "eventbus"
      && TELEMETRY_EVENT_DENYLIST.has(envelope.eventType)
    ) {
      return;
    }
    this.m_queue.enqueue(toWriteRequest(envelope));
  }

  flushPending(): Promise<void> {
    return this.m_queue.flushPending();
  }

  dispose(): Promise<void> {
    return this.m_queue.dispose();
  }
}

function toWriteRequest(envelope: TelemetryEnvelope): WriteRequest {
  const category = envelope.eventCategory
    ?? (envelope.source === "eventbus" && LIFECYCLE_EVENT_TYPES.has(envelope.eventType)
      ? "lifecycle"
      : "in-session");
  const partitionSuffix = category === "lifecycle" ? "lifecycle" : "session";

  return {
    PutRequest: {
      Item: {
        pk: { S: `${envelope.customerId}:${partitionSuffix}` },
        sk: { S: `E#${envelope.timestampMillis}#${envelope.eventUid}` },
        source: { S: envelope.source },
        event_type: { S: envelope.eventType },
        event_uid: { S: envelope.eventUid },
        user_id: { S: envelope.originatingUserId },
        ts: { N: String(envelope.timestampMillis) },
        props_json: { S: envelope.propsJson ?? "" },
      },
    },
  };
}
